type SourceRangeLike = {
    inSec?: number | null;
    outSec?: number | null;
};

type PlacedSourceSegmentLike = {
    offsetSec?: number | null;
    playbackRate?: number | null;
    range?: SourceRangeLike | null;
};

export type PlacedSourceEntryLike = PlacedSourceSegmentLike & {
    segments?: PlacedSourceSegmentLike[] | null;
};

type NormalizedPlacedSourceSegment = {
    offsetSec: number;
    playbackRate: number;
    inSec: number;
    outSec: number;
};

const normalizePlacedSourceSegments = (entry: PlacedSourceEntryLike): NormalizedPlacedSourceSegment[] => {
    const out: NormalizedPlacedSourceSegment[] = [];
    const entryAny = entry as any;
    const entrySegments: PlacedSourceSegmentLike[] | undefined =
        Array.isArray(entryAny?.segments) && entryAny.segments.length > 0 ? entryAny.segments : undefined;

    if (entrySegments) {
        for (const seg of entrySegments) {
            const offsetSec = Number(seg?.offsetSec ?? 0);
            const playbackRate = Number(seg?.playbackRate ?? entryAny?.playbackRate ?? 1.0) || 1.0;
            const inSec = Number(seg?.range?.inSec ?? entryAny?.range?.inSec ?? 0);
            const outSecRaw = seg?.range?.outSec ?? entryAny?.range?.outSec;
            const outSec = outSecRaw == null ? Number.POSITIVE_INFINITY : Number(outSecRaw);
            if (!Number.isFinite(offsetSec) || !Number.isFinite(playbackRate) || playbackRate <= 0) continue;
            if (!Number.isFinite(inSec)) continue;
            if (!(outSec === Number.POSITIVE_INFINITY || (Number.isFinite(outSec) && outSec > inSec))) continue;
            out.push({ offsetSec, playbackRate, inSec, outSec });
        }
        return out;
    }

    const offsetSec = Number(entryAny?.offsetSec ?? 0);
    const playbackRate = Number(entryAny?.playbackRate ?? 1.0) || 1.0;
    const inSec = Number(entryAny?.range?.inSec ?? 0);
    const outSecRaw = entryAny?.range?.outSec;
    const outSec = outSecRaw == null ? Number.POSITIVE_INFINITY : Number(outSecRaw);
    if (
        Number.isFinite(offsetSec) &&
        Number.isFinite(playbackRate) &&
        playbackRate > 0 &&
        Number.isFinite(inSec) &&
        (outSec === Number.POSITIVE_INFINITY || (Number.isFinite(outSec) && outSec > inSec))
    ) {
        out.push({ offsetSec, playbackRate, inSec, outSec });
    }

    return out;
};

export const mapPlacedSourceEntryClipLocalToSourceTime = (
    entry: PlacedSourceEntryLike,
    clipLocalTime: number
): number | null => {
    const segments = normalizePlacedSourceSegments(entry);
    if (segments.length === 0) return null;

    for (const seg of segments) {
        if (clipLocalTime < seg.offsetSec) continue;
        const sourceTime = seg.inSec + (clipLocalTime - seg.offsetSec) * seg.playbackRate;
        if (sourceTime >= seg.outSec) continue;
        return sourceTime;
    }

    return null;
};

export const mapPlacedSourceEntrySourceToClipLocalTime = (
    entry: PlacedSourceEntryLike,
    sourceTime: number
): number | null => {
    const segments = normalizePlacedSourceSegments(entry);
    if (segments.length === 0) return null;

    for (const seg of segments) {
        if (sourceTime < seg.inSec) continue;
        if (sourceTime >= seg.outSec) continue;
        return seg.offsetSec + (sourceTime - seg.inSec) / seg.playbackRate;
    }

    return null;
};
